import { useParams, useSearchParams } from "react-router-dom";

import { useGetRespondentSession, type SessionInfo } from "@wellcheck/api-client";

/**
 * useRespondSession — 回答者セッションを確認する。
 *
 * サーバ側 (backend/respondents.VerifyHandler, ADR-0021/0023) が `?t=<token>` を検証し、
 * 成功なら HttpOnly セッション Cookie を発行した上で `t` を除いた本 URL へ 303 リダイレクトする。
 * そのため本 hook は URL から token を扱わず、Cookie の有効性を
 * `GET /api/respondent-session` (認証は Cookie 自動送信、credentials: same-origin) に
 * 問い合わせるだけでよい。
 *
 * campaignId/respondentId は表示・エラー文脈に使うほか、session API のレスポンスの ID と
 * 突合する (ADR-0023 追記)。ブラウザに別回答者の有効なセッション Cookie が残っていると、
 * 無効な token のリンクを開いても SPA がその Cookie で別人の回答画面を表示してしまうため。
 *
 * 回答期限超過 (ADR-0032) は verify handler が Cookie を発行せず、303 redirect に
 * `?reason=answer_closed` を付与して表現する (backend/handlers/respondents_verify.go
 * answerClosedRedirectQuery)。session API 自体にはこの情報がないため、URL の query を
 * react-router の宣言的 API (useSearchParams) で読み取り、"invalid" (トークン期限切れ等、
 * 再送すれば回復する) と区別する。
 *
 * `reason=answer_closed` の時点で終端状態 (answer_closed) が URL だけで確定しており、
 * Cookie も verify handler 側で発行されていないため 401 になることが自明。
 * `enabled: !isAnswerClosed && !isAbandoned` で GET /api/respondent-session 自体を発火させず、
 * 結果を捨てるだけの無駄なリクエストを避ける。
 *
 * `reason=respondent_abandoned` (ADR-0054 Decision 3) も同じ仕組みで表現される。未受検にされた
 * respondent が verify handler に到達すると、Cookie を発行せず同 reason 付きで redirect する
 * (backend/handlers/respondents_verify.go abandonedRedirectQuery)。
 */
export interface RespondSessionParams {
  campaignId: string;
  respondentId: string;
}

export type RespondSessionState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "answer_closed" }
  | { status: "abandoned" }
  | { status: "valid"; session: SessionInfo };

export function useRespondSession(): RespondSessionParams & { session: RespondSessionState } {
  const { campaignId, respondentId } = useParams<"campaignId" | "respondentId">();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get("reason");
  const isAnswerClosed = reason === "answer_closed";
  const isAbandoned = reason === "respondent_abandoned";

  const query = useGetRespondentSession({
    query: {
      // 401 はリトライ対象外 (無効セッションを再試行しても状態は変わらない)。
      retry: false,
      select: (response) => (response.status === 200 ? response.data : undefined),
      // answer_closed / respondent_abandoned は URL だけで終端状態が確定しているため
      // fetch 自体をスキップする。
      enabled: !isAnswerClosed && !isAbandoned,
    },
  });

  const session: RespondSessionState = isAnswerClosed
    ? { status: "answer_closed" }
    : isAbandoned
      ? { status: "abandoned" }
      : query.isLoading
        ? { status: "loading" }
        : query.isError || !query.data
          ? { status: "invalid" }
          : query.data.campaignRecordId !== campaignId ||
              query.data.respondentRecordId !== respondentId
            ? { status: "invalid" }
            : { status: "valid", session: query.data };

  return {
    campaignId: campaignId ?? "",
    respondentId: respondentId ?? "",
    session,
  };
}
